import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import { useSearch } from '@/bundle/shared/hooks/useSearch';
import { Box } from '@/components/Box/Box';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { Paginator } from '@/components/Paginator/Paginator';
import { useUser } from '@/context/userContext';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { InvoiceLinkType } from '@/types/invoiceVaultTypes';
import { useState } from 'react';
import { DisputeModal } from './DisputeModal';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { INVOICE_VAULT_QUERY_KEYS, loadInvoiceLinks } from '../../../api/api';
import { getListData } from '@/helpers/helpers';
import { InvoiceLinksList } from './InvoiceLinksList';

type ParamsType = { q?: string; status?: string; page?: number; limit?: number };

export const InvoiceLinks = () => {
  const queryClient = useQueryClient();
  const searchParams = useGetSearchParams<ParamsType>();
  const { search, clearSearch, submitSearch, setSearch } = useSearch('q');
  const { isMasterAdminRole } = useUser();
  const [dispute, setDispute] = useState<InvoiceLinkType | null>(null);

  const { data: listResponse, isPending } = useQuery({
    queryKey: [INVOICE_VAULT_QUERY_KEYS.links, searchParams],
    queryFn: () => loadInvoiceLinks(searchParams),
    placeholderData: keepPreviousData,
  });
  const { list, totalCount, hasPagination } = getListData(listResponse?.body, searchParams);
  const invalidateLinks = () => queryClient.invalidateQueries({ queryKey: [INVOICE_VAULT_QUERY_KEYS.links] });

  return (
    <Box marginTop='20px'>
      <Box display='flex' justifyContent='space-between' flexDirection='column' gap='20px'>
        <SearchInput
          name='invoice-search'
          value={search}
          width='420px'
          placeholder='Invoice number or payer'
          onChange={(value) => setSearch(value)}
          onClear={clearSearch}
          onSubmit={submitSearch}
          hasErrorComponent={false}
        />
        <InvoiceLinksList list={list} isLoading={isPending} onChanged={invalidateLinks} onViewDispute={setDispute} />
      </Box>

      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}

      {dispute && (
        <DisputeModal
          link={dispute}
          canResolve={isMasterAdminRole}
          onClose={() => setDispute(null)}
          onResolved={() => {
            setDispute(null);
            invalidateLinks();
          }}
        />
      )}
    </Box>
  );
};
